import type { ElementType } from 'react';
import { TrendingUp, TrendingDown, Minus } from 'lucide-react';
import { cn } from '@shared/lib/utils';

interface StatCardProps {
  title: string;
  value: string | number;
  description?: string;
  icon?: ElementType;
  trend?: {
    value: number;
    label?: string;
  };
  /** Accent color for the icon bubble */
  accent?: 'primary' | 'violet' | 'emerald' | 'amber' | 'rose' | 'sky';
  className?: string;
}

const ACCENT_CLASSES: Record<NonNullable<StatCardProps['accent']>, string> = {
  primary: 'bg-primary/10 text-primary',
  violet:  'bg-primary/10 text-primary', // legacy alias → Orlenn orange
  emerald: 'bg-emerald-500/10 text-emerald-600',
  amber:   'bg-amber-500/10 text-amber-600',
  rose:    'bg-rose-500/10 text-rose-600',
  sky:     'bg-sky-500/10 text-sky-600',
};

export function StatCard({
  title,
  value,
  description,
  icon: Icon,
  trend,
  accent = 'primary',
  className,
}: StatCardProps) {
  const isPositive = trend && trend.value > 0;
  const isNegative = trend && trend.value < 0;

  return (
    <div
      className={cn(
        'group relative overflow-hidden rounded-xl border bg-card p-5 shadow-sm',
        'transition-all duration-200 hover:shadow-md hover:-translate-y-px',
        className,
      )}
    >
      <div className="relative flex items-start justify-between gap-3">
        <div className="flex-1 min-w-0">
          <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
            {title}
          </p>
          <p className="mt-2 text-3xl font-bold tracking-tight tabular-nums">
            {value}
          </p>
          {description && (
            <p className="mt-1 text-xs text-muted-foreground truncate">{description}</p>
          )}
          {trend && (
            <div
              className={cn(
                'mt-3 inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-semibold',
                isPositive && 'bg-emerald-50 text-emerald-700',
                isNegative && 'bg-red-50 text-red-600',
                !isPositive && !isNegative && 'bg-muted text-muted-foreground',
              )}
            >
              {isPositive && <TrendingUp className="h-3 w-3" />}
              {isNegative && <TrendingDown className="h-3 w-3" />}
              {!isPositive && !isNegative && <Minus className="h-3 w-3" />}
              {trend.value > 0 ? '+' : ''}{trend.value}%
              {trend.label && <span className="font-normal opacity-80 ml-0.5">{trend.label}</span>}
            </div>
          )}
        </div>

        {Icon && (
          <div
            className={cn(
              'flex h-11 w-11 shrink-0 items-center justify-center rounded-xl',
              'transition-transform duration-200 group-hover:scale-110',
              ACCENT_CLASSES[accent],
            )}
          >
            <Icon className="h-5 w-5" />
          </div>
        )}
      </div>
    </div>
  );
}
